import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { COMPANY_LIST, COMPANIES, companyLabel } from "../companies";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Megaphone, Plus, Trash2 } from "lucide-react";

const TAGS = ["HR", "EVENTI", "WELFARE", "IT", "URGENTE"];
const TAG_COLORS = { HR: "#16345c", EVENTI: "#8e44ad", WELFARE: "#009640", IT: "#0d6cb8", URGENTE: "#e30613" };

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy 'ore' HH:mm", { locale: it }); } catch { return iso; } };

const ComunicazioniPage = () => {
  const { isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [tag, setTag] = useState("HR");
  const [target, setTarget] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/announcements").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    setSubmitting(true);
    try {
      await api.post("/announcements", { tag, title, body, company: target });
      toast.success("Comunicazione pubblicata!");
      setTitle(""); setBody(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/announcements/${id}`); toast.success("Comunicazione eliminata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="comunicazioni-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Megaphone size={24} /> Comunicazioni</h1>
        <p className="text-[14px] text-gray-500 mb-6">Bacheca aziendale, avvisi ed eventi del gruppo.</p>

        <div className={`grid grid-cols-1 ${isAdmin ? "lg:grid-cols-3" : ""} gap-6`}>
          {isAdmin && (
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
                <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova comunicazione</h2>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Categoria</label>
                    <Select value={tag} onValueChange={setTag}>
                      <SelectTrigger className="w-full" data-testid="ann-tag"><SelectValue /></SelectTrigger>
                      <SelectContent>{TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Destinatari</label>
                    <Select value={target} onValueChange={setTarget}>
                      <SelectTrigger className="w-full" data-testid="ann-company"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le società</SelectItem>
                        {COMPANY_LIST.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-400 mt-1">Scegli se pubblicare per tutto il gruppo o per una sola società.</p>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Titolo</label>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ann-title" placeholder="Titolo dell'avviso" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Testo</label>
                    <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="ann-body" placeholder="Contenuto della comunicazione..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid="ann-submit" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                    {submitting ? "Pubblicazione..." : "Pubblica"}
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className={isAdmin ? "lg:col-span-2" : ""}>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <Megaphone size={40} className="mx-auto mb-3 opacity-40" /> Nessuna comunicazione
              </div>
            ) : (
              <div className="space-y-3" data-testid="ann-list">
                {list.map((a) => (
                  <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: TAG_COLORS[a.tag] || "#16345c" }}>{a.tag}</span>
                        {a.company && a.company !== "all" ? (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: `${COMPANIES[a.company]?.accent}1a`, color: COMPANIES[a.company]?.accent }}>{companyLabel(a.company)}</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Tutte le società</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-400">{fmt(a.created_at)}</span>
                        {isAdmin && (
                          <button onClick={() => remove(a.id)} data-testid={`ann-delete-${a.id}`} className="text-gray-400 hover:text-[#c00510] transition-colors"><Trash2 size={15} /></button>
                        )}
                      </div>
                    </div>
                    <h3 className="text-[16px] font-bold text-[#16345c] leading-snug">{a.title}</h3>
                    {a.body && <p className="text-[14px] text-gray-600 mt-1 leading-relaxed">{a.body}</p>}
                    {a.author_name && <div className="text-[12px] text-gray-400 mt-2">Pubblicato da {a.author_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ComunicazioniPage;
